import React from 'react';
import {
  MapPin,
  Calendar,
  Users,
  Trees,
  Sparkles,
  Flower2,
  Brain,
  Rocket,
  HeartHandshake,
  Wallet,
  CheckCircle2,
} from 'lucide-react';
import './AboutUs.css';

const AboutUs: React.FC = () => {
  return (
    <div className="about-us">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <span className="hero-badge">台東延平鄉｜森林學校</span>
          <h1>三天兩夜｜回到生命根本的旅程</h1>
          <p>
            這不是一所追逐成績的學校，
            <br />
            而是一個讓人重新找回「自己」的地方。
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Introduction */}
        <section className="section">
          <div className="section-header">
            <Trees className="section-icon" />
            <h2>活動介紹</h2>
          </div>

          <div className="company-intro">
            <div className="intro-text">
              <p>不是一所追逐成績的學校，而是一個讓人重新找回「自己」的地方。</p>
              <p>
                在森林裡，我們一起安靜下來、重新思考、開始創造，
                並學習與自然、與生活、與自己重新連結。
              </p>
              <p>
                這三天兩夜，不只是參加一個活動，
                而是一段回到生命根本的旅程。
              </p>
            </div>
          </div>
        </section>

        {/* Core Learning */}
        <section className="section">
          <div className="section-header">
            <Sparkles className="section-icon" />
            <h2>在森林裡，學會幾件一生受用的事</h2>
          </div>

          <div className="philosophy-grid forest-grid">
            <div className="philosophy-card">
              <Flower2 className="card-icon" />
              <h3>安靜下來｜禪定</h3>
              <p>
                不是宗教，而是讓心回到清明的方法。
                在安靜中，看見真正的自己。
              </p>
            </div>

            <div className="philosophy-card">
              <Brain className="card-icon" />
              <h3>重新思考｜第一性原理</h3>
              <p>
                不再只是接受答案，
                而是學會從根本理解世界。
              </p>
            </div>

            <div className="philosophy-card">
              <Rocket className="card-icon" />
              <h3>開始創造｜創業精神</h3>
              <p>
                把想法變成現實，
                培養行動力與承擔的能力。
              </p>
            </div>

            <div className="philosophy-card">
              <Trees className="card-icon" />
              <h3>回歸生活｜健康與自然</h3>
              <p>
                吃自然的食物、過有節奏的生活，
                重新與土地連結。
              </p>
            </div>

            <div className="philosophy-card">
              <Wallet className="card-icon" />
              <h3>放下焦慮｜安心理財</h3>
              <p>
                不盯盤、不焦慮，
                建立長期穩定的財富觀。
              </p>
            </div>

            <div className="philosophy-card highlight-card">
              <HeartHandshake className="card-icon" />
              <h3>我們想培養的，不是贏家</h3>
              <p className="highlight-text">
                而是「根深的人」
                <br />
                因為——
                <br />
                根深之後，繁茂自來。
              </p>
            </div>
          </div>
        </section>

        {/* Activity Info */}
        <section className="section">
          <div className="section-header">
            <MapPin className="section-icon" />
            <h2>活動資訊</h2>
          </div>

          <div className="info-grid">
            <div className="info-card">
              <div className="info-title">
                <MapPin className="info-icon" />
                <h3>地點</h3>
              </div>
              <p>台東延平鄉（森林場域）</p>
            </div>

            <div className="info-card">
              <div className="info-title">
                <Calendar className="info-icon" />
                <h3>時間</h3>
              </div>
              <p>三天兩夜（可填寫日期）</p>
            </div>

            <div className="info-card">
              <div className="info-title">
                <Users className="info-icon" />
                <h3>對象</h3>
              </div>
              <ul className="info-list">
                <li>想重新整理人生方向的人</li>
                <li>對身心靈、創業、理財有興趣者</li>
                <li>希望暫停忙碌、回到內在的人</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Activity Content */}
        <section className="section">
          <div className="section-header">
            <CheckCircle2 className="section-icon" />
            <h2>活動內容（範例）</h2>
          </div>

          <div className="content-card">
            <div className="activity-list-grid">
              <ul className="benefit-list">
                <li>禪定體驗（晨間與夜間）</li>
                <li>第一性原理思考練習</li>
                <li>創業與人生對談</li>
              </ul>
              <ul className="benefit-list">
                <li>自然飲食與生活體驗</li>
                <li>安心理財分享</li>
                <li>森林散步與靜心時間</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Takeaway */}
        <section className="section">
          <div className="section-header">
            <Sparkles className="section-icon" />
            <h2>這三天，你會帶走的是</h2>
          </div>

          <div className="content-card takeaway-card">
            <p className="takeaway-main">
              不是知識，
              <br />
              而是一種新的生活方式。
            </p>
          </div>
        </section>

        {/* Tagline */}
        <section className="section">
          <div className="section-header">
            <HeartHandshake className="section-icon" />
            <h2>一句話說明這個活動</h2>
          </div>

          <div className="content-card quote-card">
            <p>👉 這不是課程</p>
            <p>👉 這是一段「回到自己」的旅程</p>
          </div>
        </section>

        {/* CTA */}
        <section className="section">
          <div className="section-header">
            <Users className="section-icon" />
            <h2>立即報名</h2>
          </div>

          <div className="cta-card">
            <div className="cta-points">
              <p>👉 名額有限（建議 6–12 人）</p>
              <p>👉 小班制深度陪伴</p>
              <p>👉 點擊報名，預約你的三天兩夜</p>
            </div>

            <button className="cta-button">
              Accupass 報名按鈕
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;